import { DATE_FIELD_KEYS, type DateFieldKey, FIELD_KEYS } from './field-keys';
import { Row } from './multi-update-types';

//   Date dependencies used by validation:
//  - same-location pairs: from <= until
//  - cross-location order: pickup.* precedes drop.*
//  - publish-until >= pickup-until (when that check is enabled)

export const DATE_DEPS: Partial<Record<DateFieldKey, DateFieldKey[]>> = {
	[FIELD_KEYS.PICKUP_DATE_FROM]: [
		FIELD_KEYS.PICKUP_DATE_UNTIL,
		FIELD_KEYS.DROP_DATE_FROM,
	],
	[FIELD_KEYS.PICKUP_DATE_UNTIL]: [
		FIELD_KEYS.PICKUP_DATE_FROM,
		FIELD_KEYS.DROP_DATE_UNTIL,
	],
	[FIELD_KEYS.DROP_DATE_FROM]: [
		FIELD_KEYS.PICKUP_DATE_FROM,
		FIELD_KEYS.DROP_DATE_UNTIL,
	],
	[FIELD_KEYS.DROP_DATE_UNTIL]: [
		FIELD_KEYS.DROP_DATE_FROM,
		FIELD_KEYS.PICKUP_DATE_UNTIL,
	],
};

// Returns true if the given key already has either stored or user-entered data.
const hasValue = (rowMap: Map<string, Row>, key: DateFieldKey): boolean => {
	const row = rowMap.get(key);
	const { currentValues, value } = row ?? {};

	if (Array.isArray(currentValues) && currentValues.length > 0) {
		return true;
	}
	if (value == null) {
		return false;
	}

	if (value instanceof Date) {
		return true;
	}

	if (typeof value === 'number') {
		return true;
	}
	if (typeof value === 'string') {
		return value.trim().length > 0;
	}

	if (Array.isArray(value)) {
		return value.length > 0;
	}

	return false;
};

/**
 * Compute all date keys we must fetch now.
 * Rule:
 * 1) If a row exists for a date key but has no currentValues → fetch it.
 * 2) For every selected date key, also fetch its dependent keys if they have no value.
 * 3) Add publish-until dependency when validation is active.
 * 4) Publishing checks pick-up-until and publish-until against "now", so fetch
 *    both even when the user edits neither.
 */
export const computeNeededDateKeys = ({
	rowMap,
	selectedDateKeys,
	skipPublishUntilValidation,
	isPublishAction = false,
}: {
	rowMap: Map<string, Row>;
	selectedDateKeys: Set<DateFieldKey>;
	skipPublishUntilValidation: boolean;
	isPublishAction?: boolean;
}): Set<DateFieldKey> => {
	const needed = new Set<DateFieldKey>();

	if (isPublishAction) {
		const publishTimeKeys = [
			FIELD_KEYS.PICKUP_DATE_UNTIL,
			FIELD_KEYS.PUBLISH_UNTIL,
		];
		for (const key of publishTimeKeys) {
			if (!hasValue(rowMap, key)) {
				needed.add(key);
			}
		}
	}

	for (const key of DATE_FIELD_KEYS) {
		// 1) Missing baselines
		if (rowMap.has(key) && !hasValue(rowMap, key)) {
			needed.add(key);
		}

		// 2) Dependencies for selected keys
		if (selectedDateKeys.has(key)) {
			const deps = [...(DATE_DEPS[key] ?? [])];
			if (key === FIELD_KEYS.PICKUP_DATE_UNTIL && !skipPublishUntilValidation) {
				deps.push(FIELD_KEYS.PUBLISH_UNTIL);
			}
			// Reverse dependency: validating "publish until <= pick-up until" needs
			// the pick-up-until baseline even when only publish-until is edited.
			if (key === FIELD_KEYS.PUBLISH_UNTIL && !skipPublishUntilValidation) {
				deps.push(FIELD_KEYS.PICKUP_DATE_UNTIL);
			}

			for (const dep of deps) {
				if (!hasValue(rowMap, dep)) {
					needed.add(dep);
				}
			}
		}
	}

	return needed;
};

// Builds a per-key predicate for enabling queries.
export const makeEnabledFor =
	(neededDateKeys: Set<DateFieldKey>, groupId?: number | string | null) =>
	(key: DateFieldKey) =>
		Boolean(groupId) && neededDateKeys.has(key);
